import type { TreatmentRoomSerializer } from "@/types/treatmentRoom"

export interface TreatmentRoomBedVersion {
  revision: number
  assignment: string
  patientUuid?: string
}

export interface TreatmentRoomWriteContext extends TreatmentRoomBedVersion {
  epoch: string
}

export interface TreatmentRoomSyncPayload {
  protocol: 1
  epoch: string
  revision: number
  bedVersions: Record<string, TreatmentRoomBedVersion>
  items: TreatmentRoomSerializer[]
  deletedBeds?: string[]
}

export class TreatmentRoomSyncState {
  epoch: string | null = null
  floor = -1
  versions: Record<string, TreatmentRoomBedVersion> = {}
  items: Record<string, TreatmentRoomSerializer> = {}
  private retiredEpochs = new Set<string>()

  applySnapshot(snapshot: TreatmentRoomSyncPayload): boolean {
    if (this.retiredEpochs.has(snapshot.epoch)) return false
    if (this.epoch !== snapshot.epoch) {
      if (this.epoch) this.retiredEpochs.add(this.epoch)
      this.epoch = snapshot.epoch
      this.floor = -1
      this.versions = {}
      this.items = {}
    }
    if (snapshot.revision < this.floor) return false
    const nextItems = Object.fromEntries(snapshot.items.map(item => [item.treatmentRoomBedNumber!, item]))
    const nextVersions = { ...snapshot.bedVersions }
    Object.entries(this.versions).forEach(([bed, version]) => {
      if (version.revision <= snapshot.revision) return
      nextVersions[bed] = version
      if (this.items[bed]) nextItems[bed] = this.items[bed]
      else delete nextItems[bed]
    })
    this.floor = snapshot.revision
    this.items = nextItems
    this.versions = nextVersions
    return true
  }

  applyDelta(delta: TreatmentRoomSyncPayload): string[] | null {
    if (this.epoch !== delta.epoch) return this.retiredEpochs.has(delta.epoch) ? [] : null
    if (delta.revision <= this.floor) return []
    const itemMap = new Map(delta.items.map(item => [item.treatmentRoomBedNumber, item]))
    const deleted = new Set(delta.deletedBeds || [])
    const changed: string[] = []
    Object.entries(delta.bedVersions).forEach(([bed, version]) => {
      if (version.revision <= Math.max(this.floor, this.versions[bed]?.revision ?? -1)) return
      const item = itemMap.get(bed)
      if (!deleted.has(bed) && !item) return
      this.versions[bed] = version
      if (deleted.has(bed)) delete this.items[bed]
      else this.items[bed] = item!
      changed.push(bed)
    })
    return changed
  }

  expected(bed: string) {
    return this.epoch ? { epoch: this.epoch, ...(this.versions[bed] || { revision: 0, assignment: "" }) } : undefined
  }
}
